import type { TFunction } from "i18next";

import { APIFilterOp } from "shared/api/utils";

import { FilterOperator, FilterType } from "./types";

export const FILTER_OPERATOR_TO_API_FILTER: Record<
  FilterOperator,
  APIFilterOp
> = {
  [FilterOperator.IN]: "in",
  [FilterOperator.NOT_IN]: "nin",
  [FilterOperator.GREATER_OR_EQUAL]: "gte",
  [FilterOperator.GREATER_THAN]: "gt",
  [FilterOperator.LESS_OR_EQUAL]: "lte",
  [FilterOperator.LESS_THAN]: "lt",
  [FilterOperator.CONTAINS]: "like",
  [FilterOperator.NOT_CONTAINS]: "nlike",
  [FilterOperator.MATCH]: "match",
  [FilterOperator.STARTS_WITH]: "startswith",
  [FilterOperator.NOT_STARTS_WITH]: "nstartswith",
  [FilterOperator.BETWEEN]: "between",
  // Do not use these two in the UI as they are not handled in FilterTypeWrap.tsx meaning they are not exposed in the UI at all.
  // These can still be used in the API though, so we allow them to be used for creating resources, but convert it to "in"
  // operator before displaying / using in the UI in filterBuilderQueryToFilterBuilderState
  [FilterOperator.EQUALS]: "eq",
  [FilterOperator.NOT_EQUALS]: "neq",
  // TODO these are actually handled elsewhere as unique cases
  [FilterOperator.NOT_FILTERED]: "isn",
  [FilterOperator.IS_EMPTY]: "is",
  [FilterOperator.IS_NOT_EMPTY]: "isn",
  [FilterOperator.IS_TRUE]: "eq",
  [FilterOperator.IS_FALSE]: "eq",
  [FilterOperator.IN_LAST]: "inlast",
  [FilterOperator.EXISTS]: "exists",
  [FilterOperator.NOT_EXISTS]: "nexists",
  [FilterOperator.OCCURS]: "occurs",
  [FilterOperator.NOT_OCCURS]: "noccurs",
  [FilterOperator.HAS_ONE_OR_MORE]: "gt",
  [FilterOperator.HAS_NONE]: "lte",
};

export const API_FILTER_TO_OPERATOR: Record<APIFilterOp, FilterOperator> = {
  in: FilterOperator.IN,
  nin: FilterOperator.NOT_IN,
  gte: FilterOperator.GREATER_OR_EQUAL,
  lte: FilterOperator.LESS_OR_EQUAL,
  gt: FilterOperator.GREATER_THAN,
  lt: FilterOperator.LESS_THAN,
  is: FilterOperator.IS_EMPTY,
  isn: FilterOperator.IS_NOT_EMPTY,
  like: FilterOperator.CONTAINS,
  nlike: FilterOperator.NOT_CONTAINS,
  match: FilterOperator.MATCH,
  eq: FilterOperator.EQUALS,
  neq: FilterOperator.NOT_EQUALS,
  inlast: FilterOperator.IN_LAST,
  between: FilterOperator.BETWEEN,
  startswith: FilterOperator.STARTS_WITH,
  nstartswith: FilterOperator.NOT_STARTS_WITH,
  occurs: FilterOperator.OCCURS,
  noccurs: FilterOperator.NOT_OCCURS,
  exists: FilterOperator.EXISTS,
  nexists: FilterOperator.NOT_EXISTS,
};

export const GT_LT_GTE_LTE_OPERATORS = [
  FilterOperator.GREATER_THAN,
  FilterOperator.GREATER_OR_EQUAL,
  FilterOperator.LESS_THAN,
  FilterOperator.LESS_OR_EQUAL,
];

export const OPERATORS_MAP: Record<FilterType, FilterOperator[][]> = {
  [FilterType.STRING]: [
    [FilterOperator.NOT_FILTERED],
    [FilterOperator.IN, FilterOperator.NOT_IN],
    [
      FilterOperator.CONTAINS,
      FilterOperator.NOT_CONTAINS,
      FilterOperator.STARTS_WITH,
      FilterOperator.NOT_STARTS_WITH,
    ],
    [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  ],
  [FilterType.DURATION]: [
    [FilterOperator.NOT_FILTERED],
    GT_LT_GTE_LTE_OPERATORS,
    [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  ],
  [FilterType.NUMBER]: [
    [FilterOperator.NOT_FILTERED],
    [FilterOperator.IN, FilterOperator.NOT_IN],
    GT_LT_GTE_LTE_OPERATORS,
    [FilterOperator.BETWEEN],
    [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  ],
  [FilterType.DATE]: [
    [FilterOperator.NOT_FILTERED],
    [FilterOperator.IN, FilterOperator.NOT_IN],
    GT_LT_GTE_LTE_OPERATORS,
    [FilterOperator.BETWEEN, FilterOperator.IN_LAST],
    [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  ],
  [FilterType.BOOLEAN]: [
    [FilterOperator.NOT_FILTERED],
    [FilterOperator.IS_TRUE, FilterOperator.IS_FALSE],
    [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  ],
  [FilterType.EXISTS]: [
    [FilterOperator.NOT_FILTERED],
    [FilterOperator.EXISTS, FilterOperator.NOT_EXISTS],
  ],
  [FilterType.OCCURS]: [
    [FilterOperator.NOT_FILTERED],
    [FilterOperator.OCCURS, FilterOperator.NOT_OCCURS],
  ],
  [FilterType.RELATION]: [
    [FilterOperator.NOT_FILTERED],
    [FilterOperator.IS_EMPTY, FilterOperator.IS_NOT_EMPTY],
  ],
};

type FilterOperatorToString = {
  [key in FilterOperator]: string;
};

export const getOperatorToSign = (t: TFunction): FilterOperatorToString => ({
  [FilterOperator.NOT_FILTERED]: t("operators.sign.not_filtered"),
  [FilterOperator.IN]: t("operators.sign.in"),
  [FilterOperator.NOT_IN]: t("operators.sign.not_in"),
  [FilterOperator.BETWEEN]: t("operators.sign.between"),
  [FilterOperator.GREATER_OR_EQUAL]: t("operators.sign.greater_or_equal"),
  [FilterOperator.GREATER_THAN]: t("operators.sign.greater_than"),
  [FilterOperator.LESS_OR_EQUAL]: t("operators.sign.less_or_equal"),
  [FilterOperator.LESS_THAN]: t("operators.sign.less_than"),
  [FilterOperator.IS_EMPTY]: t("operators.sign.is_empty"),
  [FilterOperator.IS_NOT_EMPTY]: t("operators.sign.is_not_empty"),
  [FilterOperator.CONTAINS]: t("operators.sign.contains"),
  [FilterOperator.NOT_CONTAINS]: t("operators.sign.not_contains"),
  [FilterOperator.MATCH]: t("operators.sign.match"),
  [FilterOperator.IN_LAST]: t("operators.sign.in_last"),
  [FilterOperator.STARTS_WITH]: t("operators.sign.starts_with"),
  [FilterOperator.NOT_STARTS_WITH]: t("operators.sign.not_starts_with"),
  [FilterOperator.IS_TRUE]: t("operators.sign.is_true"),
  [FilterOperator.IS_FALSE]: t("operators.sign.is_false"),
  [FilterOperator.EQUALS]: t("operators.sign.eq"),
  [FilterOperator.NOT_EQUALS]: t("operators.sign.neq"),
  [FilterOperator.EXISTS]: t("operators.sign.exists"),
  [FilterOperator.NOT_EXISTS]: t("operators.sign.nexists"),
  [FilterOperator.OCCURS]: t("operators.sign.occurs"),
  [FilterOperator.NOT_OCCURS]: t("operators.sign.noccurs"),
  [FilterOperator.HAS_ONE_OR_MORE]: t("operators.sign.has_one_or_more"),
  [FilterOperator.HAS_NONE]: t("operators.sign.has_none"),
});

// This is used in FiltersOverviews to display the filter operator
export const getOperatorToWords = (t: TFunction): FilterOperatorToString => ({
  ...getOperatorToSign(t),
  [FilterOperator.GREATER_OR_EQUAL]: t("operators.words.greater_or_equal"),
  [FilterOperator.GREATER_THAN]: t("operators.words.greater_than"),
  [FilterOperator.LESS_OR_EQUAL]: t("operators.words.less_or_equal"),
  [FilterOperator.LESS_THAN]: t("operators.words.less_than"),
  [FilterOperator.IS_EMPTY]: t("operators.words.is_empty"),
  [FilterOperator.IS_NOT_EMPTY]: t("operators.words.is_not_empty"),
  [FilterOperator.EQUALS]: t("operators.words.eq"),
  [FilterOperator.NOT_EQUALS]: t("operators.words.neq"),
  [FilterOperator.EXISTS]: t("operators.words.exists"),
  [FilterOperator.NOT_EXISTS]: t("operators.words.nexists"),
  [FilterOperator.OCCURS]: t("operators.words.occurs"),
  [FilterOperator.NOT_OCCURS]: t("operators.words.noccurs"),
});

// Default will be cleanString(filterOperator) if not specified
// This is used when displaying filter operator in dropdowns
export const getOperatorToLabel = (
  t: TFunction
): Partial<FilterOperatorToString> => ({
  [FilterOperator.NOT_FILTERED]: t("operators.label.not_filtered"),
  [FilterOperator.IN]: t("operators.label.in"),
  [FilterOperator.NOT_IN]: t("operators.label.not_in"),
  [FilterOperator.CONTAINS]: t("operators.label.contains"),
  [FilterOperator.NOT_CONTAINS]: t("operators.label.not_contains"),
  [FilterOperator.BETWEEN]: t("operators.label.between"),
  [FilterOperator.IN_LAST]: t("operators.label.in_last"),
  [FilterOperator.STARTS_WITH]: t("operators.label.starts_with"),
  [FilterOperator.NOT_STARTS_WITH]: t("operators.label.not_starts_with"),
  [FilterOperator.GREATER_OR_EQUAL]: t("operators.label.greater_or_equal"),
  [FilterOperator.GREATER_THAN]: t("operators.label.greater_than"),
  [FilterOperator.LESS_OR_EQUAL]: t("operators.label.less_or_equal"),
  [FilterOperator.LESS_THAN]: t("operators.label.less_than"),
  [FilterOperator.EXISTS]: t("operators.label.exists"),
  [FilterOperator.NOT_EXISTS]: t("operators.label.nexists"),
  [FilterOperator.OCCURS]: t("operators.label.occurs"),
  [FilterOperator.NOT_OCCURS]: t("operators.label.noccurs"),
  [FilterOperator.IS_TRUE]: t("operators.label.is_true"),
  [FilterOperator.IS_FALSE]: t("operators.label.is_false"),
  [FilterOperator.IS_EMPTY]: t("operators.label.is_empty"),
  [FilterOperator.IS_NOT_EMPTY]: t("operators.label.is_not_empty"),
});

export const getOperatorToLabelOverrideByFilterType = (
  t: TFunction
): Partial<Record<FilterType, Partial<FilterOperatorToString>>> => ({
  [FilterType.DATE]: {
    [FilterOperator.GREATER_THAN]: t(
      "operators.label_overrides.date.greater_than"
    ),
    [FilterOperator.GREATER_OR_EQUAL]: t(
      "operators.label_overrides.date.greater_or_equal"
    ),
    [FilterOperator.LESS_THAN]: t("operators.label_overrides.date.less_than"),
    [FilterOperator.LESS_OR_EQUAL]: t(
      "operators.label_overrides.date.less_or_equal"
    ),
  },
});

export const DEFAULT_FILTER_TYPE: FilterType = FilterType.STRING;

export const DEFAULT_OPERATOR_PER_TYPE: Record<FilterType, FilterOperator> = {
  [FilterType.STRING]: FilterOperator.NOT_FILTERED,
  [FilterType.DURATION]: FilterOperator.NOT_FILTERED,
  [FilterType.NUMBER]: FilterOperator.NOT_FILTERED,
  [FilterType.DATE]: FilterOperator.NOT_FILTERED,
  [FilterType.BOOLEAN]: FilterOperator.NOT_FILTERED,
  [FilterType.EXISTS]: FilterOperator.NOT_FILTERED,
  [FilterType.OCCURS]: FilterOperator.NOT_FILTERED,
  [FilterType.RELATION]: FilterOperator.NOT_FILTERED,
};

/**
 * When pasting a list of values, we split on this delimiter(s) when using "is any of" filter.
 * "\n" => new line
 * "\t" => tab
 * "," => comma
 */
export const FILTER_PASTE_DELIMITER: RegExp = /\n|\t|,/;

export const ANY_ALL_INDEX = -1;
